import { Delete, Edit } from '@mui/icons-material';
import { Box } from '@mui/material';
import {
  GridCellModes,
  type GridColDef,
  type GridEventListener,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDataItem, useUpdateDataItem } from '~/api/data-items';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { useDataGridEditing } from '~/hooks/data-grid';
import { useConfirmDialog } from '~/hooks/dialogs';
import { usePaginationParams } from '~/hooks/pagination';
import type { DataSourceData__DataSource as DataSource } from '../queries/data.generated';

interface DataType {
  id: number;
  __enabled__?: string;
  __internalId__?: number;
  __preventDestroyReason__?: string;
  [key: string]: string | number | undefined;
}

interface UseSpreadsheetUtilsOptions {
  columnOrder?: string[];
  columnWidths?: Record<string, number>;
  orderBy?: string | null;
  orderFirst?: number;
  search?: string;
}

export const useSpreadsheetUtils = (
  dataSource: DataSource | undefined,
  highlightedId: number | undefined,
  options: UseSpreadsheetUtilsOptions = {},
) => {
  const { t } = useTranslation();
  const { columnOrder, columnWidths = {}, orderBy, orderFirst, search } = options;
  const { page, perPage } = usePaginationParams();

  const {
    cellModesModel,
    handleCellEditStart,
    handleCellFocus,
    handleCellKeyDown,
    selectedCellParams,
    setCellModesModel,
    setSelectedCellParams,
  } = useDataGridEditing();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteDataItem] = useDeleteDataItem();
  const [updateDataItem] = useUpdateDataItem();

  const handleDelete = useCallback(
    async (id: number | undefined) => {
      if (!id) return;
      if (!(await confirmDelete())) return;
      await deleteDataItem({
        variables: { input: { id }, orderBy, orderFirst, page, perPage, search },
      });
    },
    [confirmDelete, deleteDataItem, orderBy, orderFirst, page, perPage, search],
  );

  const processRowUpdate = useCallback(
    (newRow: DataType, oldRow: DataType) => {
      const {
        id: _displayId,
        __internalId__,
        __enabled__,
        __preventDestroyReason__,
        ...newData
      } = newRow;

      const {
        id: _oldDisplayId,
        __internalId__: _oldInternalId,
        __enabled__: _oldEnabled,
        __preventDestroyReason__: _oldPreventDestroyReason,
        ...oldData
      } = oldRow;

      if (!__internalId__) return oldRow;

      // Skip update if data hasn't changed
      if (JSON.stringify(newData) === JSON.stringify(oldData)) {
        return oldRow;
      }

      // Fire mutation in background (optimistic update)
      // Errors show toast via useUpdateDataItem's built-in notifications
      void updateDataItem({
        variables: {
          id: __internalId__,
          data: newData,
        },
      });

      // Return new row immediately for snappy UI
      return newRow;
    },
    [updateDataItem],
  );

  // use default MUI data grid behavior unless we are clicking the cancel button
  const handleCellEditStop = useCallback<GridEventListener<'cellEditStop'>>((params, event) => {
    if (
      params.reason !== 'cellFocusOut' ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLButtonElement)
    )
      return;
    if (event.target.innerText === 'Cancel') event.defaultMuiPrevented = true;
  }, []);

  const rows: DataType[] = useMemo(() => {
    if (!dataSource) return [];
    return dataSource.items.nodes
      .filter((x) => x.data)
      .map((x, i) => ({
        ...x.data,
        id: i + 1 + perPage * (page - 1),
        __internalId__: x.id,
        __enabled__: String(x.enabled),
        __preventDestroyReason__: x.canDestroy.reasons?.fullMessages.join(),
      }));
  }, [dataSource, page, perPage]);

  const startEditing = useCallback(
    (id: string | number, field: string) => {
      setCellModesModel({
        [id]: { [field]: { mode: GridCellModes.Edit } },
      });
    },
    [setCellModesModel],
  );

  const columns = useMemo(() => {
    if (!dataSource) return [];
    const isEditable = dataSource.kind !== 'INTEGRATION';

    const cols: GridColDef<DataType>[] = [
      {
        align: 'center',
        field: 'id',
        ...hiddenHeader(t('common.labels.row')),
        hideable: false,
        resizable: false,
        sortable: false,
        width: 50,
      },
      ...dataSource.keys.map((x) => {
        const storedWidth = columnWidths[x];
        return {
          editable: isEditable,
          field: x,
          ...(storedWidth ? { width: storedWidth } : { flex: 1 }),
          headerName: x,
          minWidth: 150,
          sortable: true,
          ...(isEditable && {
            renderCell: (params: GridRenderCellParams<DataType>) => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  height: '100%',
                  '& .edit-icon': { opacity: 0, transition: 'opacity 0.2s' },
                  '&:hover .edit-icon, &:focus-within .edit-icon': { opacity: 1 },
                }}
              >
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.value}</Box>
                <IconButton
                  label={t('common.actions.edit')}
                  className="edit-icon"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing(params.id, params.field);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Edit sx={{ fontSize: 16, color: 'primary.dark' }} />
                </IconButton>
              </Box>
            ),
          }),
        };
      }),
    ];

    if (dataSource.kind !== 'INTEGRATION') {
      cols.push({
        align: 'center',
        field: 'actions',
        hideable: false,
        type: 'actions',
        resizable: false,
        width: 50,
        getActions: ({ row }) =>
          row.__internalId__
            ? [
                <ActionButton
                  Icon={Delete}
                  color="error"
                  description={row.__preventDestroyReason__}
                  disabled={Boolean(row.__preventDestroyReason__)}
                  key="delete"
                  onClick={() => handleDelete(row.__internalId__)}
                  title={t('pages.data_sources.edit.delete_row.button')}
                />,
              ]
            : [],
      });
    }

    // Sort columns based on saved column order
    if (columnOrder && columnOrder.length > 0) {
      cols.sort((a, b) => {
        const aIndex = columnOrder.indexOf(a.field);
        const bIndex = columnOrder.indexOf(b.field);
        // If not in columnOrder, keep at end in original order
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }

    return cols;
  }, [columnOrder, columnWidths, dataSource, handleDelete, startEditing, t]);

  const highlightedIndex = useMemo(
    () =>
      highlightedId
        ? rows.findIndex((x) => x.__internalId__ === highlightedId) + 1 + perPage * (page - 1)
        : null,
    [highlightedId, page, perPage, rows],
  );

  useEffect(() => {
    if (!dataSource || selectedCellParams) return;
    setSelectedCellParams({ id: '1', field: dataSource.keys.at(0) || '' });
  }, [dataSource, selectedCellParams, setSelectedCellParams]);

  return {
    cellModesModel,
    columns,
    confirmDeleteProps,
    handleCellEditStart,
    handleCellEditStop,
    handleCellFocus,
    handleCellKeyDown,
    highlightedIndex,
    processRowUpdate,
    rows,
    setCellModesModel,
  };
};
